// src/components/maintenance/utils/maintenanceProcessor.js
import { supabaseClient } from '../../supabase';

/**
 * Process a maintenance operation with all three CSV types
 * Order: Teachers → Classes → Students/Parents
 */
export async function processMaintenanceOperation(operationId) {
  try {
    // Fetch the operation
    const { data: operation, error: fetchError } = await supabaseClient
      .from('maintenance_operations')
      .select('*')
      .eq('id', operationId)
      .single();

    if (fetchError) throw fetchError;

    if (operation.status !== 'validated') {
      throw new Error('Operation must be validated before processing');
    }

    const previewData = operation.preview_data;
    const conflictResolutions = operation.conflict_resolutions || {};

    console.log('Starting maintenance processing...');

    // STEP 1: Process Teachers
    await processTeachers(previewData.teachers);

    // STEP 2: Process Classes (after teachers exist)
    await processClasses(previewData.classes, previewData.teachers);

    // STEP 3: Get alumni class (or create it)
    const alumniClassId = await getOrCreateAlumniClass(operation.alumni_class_name);

    // STEP 4: Move graduating students to alumni
    await processGraduatingStudents(previewData.students.graduating, alumniClassId);

    // STEP 5: Process households and parents
    const householdMapping = await processHouseholdsAndParents(
      previewData,
      conflictResolutions,
      operation.invite_new_users
    );

    // STEP 6: Update existing students
    await processUpdatedStudents(previewData.students.updated, householdMapping);

    // STEP 7: Create new students
    await processNewStudents(previewData.students.new, householdMapping);

    // STEP 8: Delete students not in CSV (grades 0-6) if enabled
    if (operation.delete_students_not_in_csv) {
      await processStudentDeletions(
        previewData.studentsToDelete,
        operation.delete_students_not_in_csv
      );
    }

    // Mark operation as processed
    const { error: updateError } = await supabaseClient
      .from('maintenance_operations')
      .update({
        status: 'processed',
        processed_at: new Date().toISOString(),
      })
      .eq('id', operationId);

    if (updateError) throw updateError;

    console.log('Maintenance processing completed successfully');
    return { success: true };

  } catch (error) {
    console.error('Error processing maintenance operation:', error);

    // Mark operation as failed
    await supabaseClient
      .from('maintenance_operations')
      .update({
        status: 'failed',
        error_message: error.message,
      })
      .eq('id', operationId);

    throw error;
  }
}

/**
 * STEP 1: Process Teachers
 */
async function processTeachers(teachersPreview) {
  console.log('Processing teachers...');

  // Create new teachers (without class_id for now)
  if (teachersPreview.new.length > 0) {
    console.log(`Creating ${teachersPreview.new.length} new teachers...`);

    const teachersToInsert = teachersPreview.new.map(teacher => ({
      first_name: teacher.first_name,
      last_name: teacher.last_name,
      email: teacher.email,
      mobile: teacher.mobile,
      role: teacher.role || 'teacher',
      class_id: null, // Will be set after classes are processed
    }));

    const { error } = await supabaseClient
      .from('teachers')
      .insert(teachersToInsert);

    if (error) throw error;
  }

  // Update existing teachers (except class_id)
  if (teachersPreview.updated.length > 0) {
    console.log(`Updating ${teachersPreview.updated.length} existing teachers...`);

    for (const teacher of teachersPreview.updated) {
      const { error } = await supabaseClient
        .from('teachers')
        .update({
          first_name: teacher.first_name,
          last_name: teacher.last_name,
          email: teacher.email,
          mobile: teacher.mobile,
          role: teacher.role || 'teacher',
          class_id: null, // Reset for now, will be set after classes are processed
        })
        .eq('id', teacher.id);

      if (error) throw error;
    }
  }

  // Remove teachers
  if (teachersPreview.removed.length > 0) {
    console.log(`Removing ${teachersPreview.removed.length} teachers...`);

    const teacherIds = teachersPreview.removed.map(t => t.id);

    const { error } = await supabaseClient
      .from('teachers')
      .delete()
      .in('id', teacherIds);

    if (error) throw error;
  }
}

/**
 * STEP 2: Process Classes
 */
async function processClasses(classesPreview, teachersPreview) {
  console.log('Processing classes...');

  // Build map of class codes to be created/updated
  const classCodeToData = new Map();

  // Create new classes
  if (classesPreview.new.length > 0) {
    console.log(`Creating ${classesPreview.new.length} new classes...`);

    const classesToInsert = classesPreview.new.map(cls => ({
      class_code: cls.class_code,
      grade: cls.grade,
    }));

    const { data: insertedClasses, error } = await supabaseClient
      .from('classes')
      .insert(classesToInsert)
      .select();

    if (error) throw error;

    // Map inserted classes
    insertedClasses.forEach(cls => {
      classCodeToData.set(cls.class_code, cls);
    });
  }

  // Update existing classes
  if (classesPreview.updated.length > 0) {
    console.log(`Updating ${classesPreview.updated.length} existing classes...`);

    for (const cls of classesPreview.updated) {
      const { data: updatedClass, error } = await supabaseClient
        .from('classes')
        .update({
          class_code: cls.class_code,
          grade: cls.grade,
        })
        .eq('id', cls.id)
        .select()
        .single();

      if (error) throw error;

      classCodeToData.set(updatedClass.class_code, updatedClass);
    }
  }

  // Remove classes
  if (classesPreview.removed.length > 0) {
    console.log(`Removing ${classesPreview.removed.length} classes...`);

    const classIds = classesPreview.removed.map(c => c.id);

    const { error } = await supabaseClient
      .from('classes')
      .delete()
      .in('id', classIds);

    if (error) throw error;
  }

  // Now link teachers to their classes
  console.log('Linking teachers to classes...');

  // Get all teachers from CSV (new + updated)
  const allTeachers = [...teachersPreview.new, ...teachersPreview.updated];

  for (const teacher of allTeachers) {
    if (teacher.class_code) {
      // Find the class by class_code
      let classData = classCodeToData.get(teacher.class_code);

      if (!classData) {
        // Fetch from DB if not in our map
        const { data: fetchedClass, error } = await supabaseClient
          .from('classes')
          .select('*')
          .eq('class_code', teacher.class_code)
          .single();

        if (error) {
          console.warn(`Could not find class ${teacher.class_code} for teacher ${teacher.email}`);
          continue;
        }

        classData = fetchedClass;
      }

      // Update teacher's class_id
      const { error } = await supabaseClient
        .from('teachers')
        .update({ class_id: classData.id })
        .eq('email', teacher.email);

      if (error) {
        console.error(`Error linking teacher ${teacher.email} to class ${teacher.class_code}:`, error);
      }
    }
  }
}

/**
 * Get or create alumni class
 */
async function getOrCreateAlumniClass(alumniClassName) {
  const { data: alumniClass, error: alumniError } = await supabaseClient
    .from('classes')
    .select('*')
    .eq('class_code', alumniClassName)
    .single();

  if (alumniError || !alumniClass) {
    // Create alumni class
    const { data: newAlumniClass, error: createError } = await supabaseClient
      .from('classes')
      .insert({
        class_code: alumniClassName,
        grade: 99,
      })
      .select()
      .single();

    if (createError) throw createError;
    return newAlumniClass.id;
  }

  return alumniClass.id;
}

/**
 * STEP 4: Move graduating students to alumni
 */
async function processGraduatingStudents(graduatingStudents, alumniClassId) {
  if (!graduatingStudents || graduatingStudents.length === 0) return;

  console.log(`Moving ${graduatingStudents.length} students to alumni...`);

  const studentIds = graduatingStudents.map(s => s.id);

  const { error } = await supabaseClient
    .from('students')
    .update({
      class_id: alumniClassId,
      grade: 99,
    })
    .in('id', studentIds);

  if (error) throw error;
}

/**
 * STEP 5: Process households and parents
 */
async function processHouseholdsAndParents(previewData, conflictResolutions, inviteNewUsers) {
  console.log('Processing households and parents...');

  const householdMapping = new Map();
  const processedEmails = new Set();

  // Collect all unique households from students
  const allHouseholds = new Map();

  [...previewData.students.new, ...previewData.students.updated].forEach(student => {
    if (!allHouseholds.has(student.household_hash)) {
      allHouseholds.set(student.household_hash, {
        household_hash: student.household_hash,
        parents: student.parents,
        isNew: student.isNewHousehold,
      });
    }
  });

  // Process each household
  for (const [tempHash, householdInfo] of allHouseholds) {
    if (!householdInfo.isNew) {
      // Existing household - just map it
      householdMapping.set(tempHash, tempHash);

      // Update parent info if changed
      for (const parent of householdInfo.parents) {
        if (parent.email && !processedEmails.has(parent.email)) {
          await updateParent(parent);
          processedEmails.add(parent.email);
        }
      }
    } else {
      // New household - create parents
      const actualHash = crypto.randomUUID();
      householdMapping.set(tempHash, actualHash);

      for (const parent of householdInfo.parents) {
        if (parent.email && !processedEmails.has(parent.email)) {
          await createParent({ ...parent, household_hash: actualHash });

          if (inviteNewUsers) {
            await inviteUser(parent.email);
          }

          processedEmails.add(parent.email);
        }
      }
    }
  }

  // Handle conflict resolutions
  for (const [conflictKey, resolution] of Object.entries(conflictResolutions)) {
    const conflictIndex = parseInt(conflictKey);
    const conflict = previewData.householdConflicts[conflictIndex];

    if (!conflict) continue;

    if (resolution === 'create_new') {
      // Create new household for this student
      const newHash = crypto.randomUUID();

      // Create new parent records
      for (const parent of conflict.newParents) {
        if (parent.email) {
          await createParent({ ...parent, household_hash: newHash });

          if (inviteNewUsers) {
            await inviteUser(parent.email);
          }
        }
      }

      // Update the household mapping for this student
      householdMapping.set(conflict.newHousehold, newHash);

    } else if (resolution === 'update_existing') {
      // Update existing household with new parent info
      for (const parent of conflict.newParents) {
        if (parent.email) {
          await updateParent({ ...parent, household_hash: conflict.currentHousehold });
        }
      }

      householdMapping.set(conflict.newHousehold, conflict.currentHousehold);

    } else if (resolution === 'keep_existing') {
      // Keep the existing household, ignore new parent info
      householdMapping.set(conflict.newHousehold, conflict.currentHousehold);
    }
  }

  return householdMapping;
}

/**
 * Update existing parent
 */
async function updateParent(parentData) {
  const { error } = await supabaseClient
    .from('parents')
    .update({
      first_name: parentData.first_name,
      last_name: parentData.last_name,
      mobile: parentData.mobile,
    })
    .eq('email', parentData.email);

  if (error) throw error;
}

/**
 * Create new parent
 */
async function createParent(parentData) {
  const { error } = await supabaseClient
    .from('parents')
    .insert({
      first_name: parentData.first_name,
      last_name: parentData.last_name,
      email: parentData.email,
      mobile: parentData.mobile,
      household_hash: parentData.household_hash,
      role: 'parent',
    });

  if (error) throw error;
}

/**
 * Invite user via edge function
 */
async function inviteUser(email) {
  try {
    const { error } = await supabaseClient.functions.invoke('resetAuthUser', {
      body: { email },
    });

    if (error) {
      console.error(`Failed to invite user ${email}:`, error);
    }
  } catch (error) {
    console.error(`Error inviting user ${email}:`, error);
  }
}

/**
 * STEP 6: Update existing students
 */
async function processUpdatedStudents(updatedStudents, householdMapping) {
  if (!updatedStudents || updatedStudents.length === 0) return;

  console.log(`Updating ${updatedStudents.length} existing students...`);

  for (const student of updatedStudents) {
    const actualHouseholdHash = householdMapping.get(student.household_hash) || student.household_hash;

    const { error } = await supabaseClient
      .from('students')
      .update({
        first_name: student.first_name,
        last_name: student.last_name,
        class_id: student.class_id,
        grade: student.grade,
        household_hash: actualHouseholdHash,
      })
      .eq('id', student.id);

    if (error) throw error;
  }
}

/**
 * STEP 7: Create new students
 */
async function processNewStudents(newStudents, householdMapping) {
  if (!newStudents || newStudents.length === 0) return;

  console.log(`Creating ${newStudents.length} new students...`);

  const studentsToInsert = newStudents.map(student => {
    const actualHouseholdHash = householdMapping.get(student.household_hash) || student.household_hash;

    return {
      first_name: student.first_name,
      last_name: student.last_name,
      class_id: student.class_id,
      grade: student.grade,
      household_hash: actualHouseholdHash,
      role: 'student',
    };
  });

  const { error } = await supabaseClient
    .from('students')
    .insert(studentsToInsert);

  if (error) throw error;
}

/**
 * STEP 8: Delete students not in CSV (if enabled)
 */
async function processStudentDeletions(studentsToDelete, deleteEnabled) {
  if (!deleteEnabled || !studentsToDelete || studentsToDelete.length === 0) {
    console.log('Student deletion skipped (not enabled or no students to delete)');
    return;
  }

  console.log(`Deleting ${studentsToDelete.length} students not in CSV...`);

  const studentIds = studentsToDelete.map(s => s.id);

  const { error } = await supabaseClient
    .from('students')
    .delete()
    .in('id', studentIds);

  if (error) throw error;
}

/**
 * Rollback a processed maintenance operation
 */
export async function rollbackMaintenanceOperation(operationId) {
  try {
    // Fetch the operation
    const { data: operation, error: fetchError } = await supabaseClient
      .from('maintenance_operations')
      .select('*')
      .eq('id', operationId)
      .single();

    if (fetchError) throw fetchError;

    if (operation.status !== 'processed') {
      throw new Error('Can only rollback processed operations');
    }

    console.log('Starting rollback...');

    // Parse the rollback file paths
    const rollbackPaths = JSON.parse(operation.rollback_file_path);

    // Download all three rollback CSVs
    const [teachersData, classesData, studentsData] = await Promise.all([
      supabaseClient.storage.from('maintenance-files').download(rollbackPaths.teachers),
      supabaseClient.storage.from('maintenance-files').download(rollbackPaths.classes),
      supabaseClient.storage.from('maintenance-files').download(rollbackPaths.students_parents),
    ]);

    if (teachersData.error) throw teachersData.error;
    if (classesData.error) throw classesData.error;
    if (studentsData.error) throw studentsData.error;

    // Convert to text and parse
    const { parseCSV } = await import('./csvGenerator');

    const [teachersText, classesText, studentsText] = await Promise.all([
      teachersData.data.text(),
      classesData.data.text(),
      studentsData.data.text(),
    ]);

    const teachersCSV = parseCSV(teachersText);
    const classesCSV = parseCSV(classesText);
    const studentsParentsCSV = parseCSV(studentsText);

    console.log('Rollback CSVs parsed:', {
      teachers: teachersCSV.length,
      classes: classesCSV.length,
      students: studentsParentsCSV.length,
    });

    // STEP 1: Delete all current students and parents
    console.log('Deleting all current students and parents...');
    await supabaseClient.from('students').delete().neq('id', 0);
    await supabaseClient.from('parents').delete().neq('id', 0);

    // STEP 2: Delete all current teachers and classes
    console.log('Deleting all current teachers and classes...');
    await supabaseClient.from('teachers').delete().neq('id', 0);
    await supabaseClient.from('classes').delete().neq('id', 0);

    // STEP 3: Restore teachers (BATCH INSERT)
    console.log('Restoring teachers from rollback...');
    const teachersToRestore = teachersCSV.map(row => ({
      id: row['Teacher ID'] ? parseInt(row['Teacher ID']) : null,
      first_name: row['First Name']?.trim() || null,
      last_name: row['Last Name']?.trim() || null,
      email: row['Email']?.trim() || null,
      mobile: row['Mobile']?.trim() || null,
      role: row['Role']?.trim() || 'teacher',  // ← Use role from CSV
      class_id: null, // Will be set after classes are restored
    })).filter(t => t.id);

    // Batch insert teachers
    if (teachersToRestore.length > 0) {
      const { error } = await supabaseClient
        .from('teachers')
        .insert(teachersToRestore);
      if (error) throw error;
    }

    // STEP 4: Restore classes (BATCH INSERT)
    console.log('Restoring classes from rollback...');
    const classesToRestore = classesCSV.map(row => {
      const grade = row['Grade']?.trim();
      return {
        id: row['Class ID'] ? parseInt(row['Class ID']) : null,
        class_code: row['Class Code']?.trim() || '',
        grade: grade !== '' ? parseInt(grade) : null,
      };
    }).filter(c => c.id);

    if (classesToRestore.length > 0) {
      const { error } = await supabaseClient
        .from('classes')
        .insert(classesToRestore);
      if (error) throw error;
    }

    // STEP 5: Link teachers to classes (BATCH UPDATE)
    console.log('Linking teachers to classes...');

    // Build a map of class_code -> class_id
    const { data: allClasses } = await supabaseClient
      .from('classes')
      .select('id, class_code, grade');

    const classCodeMap = new Map(allClasses.map(c => [c.class_code, c.id]));

    // Prepare teacher updates
    for (const row of teachersCSV) {
      const teacherId = row['Teacher ID'] ? parseInt(row['Teacher ID']) : null;
      const classCode = row['Class Code (Owner)']?.trim();

      if (teacherId && classCode && classCodeMap.has(classCode)) {
        await supabaseClient
          .from('teachers')
          .update({ class_id: classCodeMap.get(classCode) })
          .eq('id', teacherId);
      }
    }

    // STEP 6: Build parent and student data structures
    console.log('Building parent and student data structures...');

    const parentsByEmail = new Map();
    const studentsToRestore = [];
    const householdMap = new Map(); // householdKey -> household_hash

    for (const row of studentsParentsCSV) {
      const studentId = row['Student ID'] ? parseInt(row['Student ID']) : null;
      const firstName = row['First Name']?.trim();
      const lastName = row['Last Name']?.trim();
      const classCode = row['Class Code']?.trim();

      if (!studentId || !firstName || !lastName || !classCode) continue;

      const classId = classCodeMap.get(classCode);
      if (!classId) {
        console.warn(`Class not found: ${classCode}`);
        continue;
      }

      // Extract parent emails for this student
      const parentEmails = [];
      const headers = Object.keys(row);
      let parentIndex = 1;

      while (headers.includes(`Parent ${parentIndex} Email`)) {
        const email = row[`Parent ${parentIndex} Email`]?.trim().toLowerCase();
        if (email) {
          parentEmails.push(email);

          // Store parent data
          if (!parentsByEmail.has(email)) {
            parentsByEmail.set(email, {
              first_name: row[`Parent ${parentIndex} First Name`]?.trim() || null,
              last_name: row[`Parent ${parentIndex} Last Name`]?.trim() || null,
              email: email,
              mobile: row[`Parent ${parentIndex} Mobile`]?.trim() || null,
              role: 'parent',
            });
          }
        }
        parentIndex++;
      }

      // Create household key from sorted parent emails
      const householdKey = parentEmails.sort().join('|');

      // Get or create household_hash for this set of parents
      let household_hash;
      if (householdMap.has(householdKey)) {
        household_hash = householdMap.get(householdKey);
      } else {
        household_hash = crypto.randomUUID();
        householdMap.set(householdKey, household_hash);
      }

      // Find the grade from classes
      const classData = allClasses.find(c => c.id === classId);

      studentsToRestore.push({
        id: studentId,
        first_name: firstName,
        last_name: lastName,
        class_id: classId,
        grade: classData?.grade || null,
        household_hash: household_hash,
        role: 'student',
      });
    }

    // STEP 7: Restore parents (BATCH INSERT)
    console.log(`Restoring ${parentsByEmail.size} unique parents...`);

    const parentsToInsert = [];
    for (const [householdKey, household_hash] of householdMap) {
      const emails = householdKey.split('|');
      for (const email of emails) {
        const parentData = parentsByEmail.get(email);
        if (parentData) {
          parentsToInsert.push({
            ...parentData,
            household_hash: household_hash,
          });
        }
      }
    }

    // Batch insert in chunks of 1000
    const chunkSize = 1000;
    for (let i = 0; i < parentsToInsert.length; i += chunkSize) {
      const chunk = parentsToInsert.slice(i, i + chunkSize);
      const { error } = await supabaseClient
        .from('parents')
        .insert(chunk);
      if (error) throw error;
      console.log(`Inserted parents ${i + 1} to ${Math.min(i + chunkSize, parentsToInsert.length)}`);
    }

    // STEP 8: Restore students (BATCH INSERT)
    console.log(`Restoring ${studentsToRestore.length} students...`);

    for (let i = 0; i < studentsToRestore.length; i += chunkSize) {
      const chunk = studentsToRestore.slice(i, i + chunkSize);
      const { error } = await supabaseClient
        .from('students')
        .insert(chunk);
      if (error) throw error;
      console.log(`Inserted students ${i + 1} to ${Math.min(i + chunkSize, studentsToRestore.length)}`);
    }

    // Mark operation as rolled back
    const { error: updateError } = await supabaseClient
      .from('maintenance_operations')
      .update({
        status: 'rolled_back',
      })
      .eq('id', operationId);

    if (updateError) throw updateError;

    console.log('Rollback completed successfully');
    return { success: true };

  } catch (error) {
    console.error('Error during rollback:', error);

    // Mark operation as rollback failed
    await supabaseClient
      .from('maintenance_operations')
      .update({
        status: 'rollback_failed',
        error_message: `Rollback error: ${error.message}`,
      })
      .eq('id', operationId);

    throw error;
  }
}